import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PageHeader from '../print-layout/PageHeader';
import { type Layout, type LayoutItem } from 'react-grid-layout';
import ReactGridLayout from 'react-grid-layout';
import { BlockContainer } from '../../../../../venn-components/src/blocks/BlockContainer';
import PageFooter from '../print-layout/PageFooter';
import { type Page } from 'venn-components';
import {
  dimensions,
  StudioContext,
  StudioShimmerBlock,
  UserContext,
  calcGridItemWidth,
  useAppPrintMode,
  ReportLabBlockContainer,
} from 'venn-components';
import { analyticsService, PageType, SpecialCssClasses, useHasFF } from 'venn-utils';
import { DROPPING_ITEM, useOnDragOrResize, useUpdatePage } from './Shared';
import { useRecoilCallback, useRecoilValue, useSetRecoilState, waitForAll } from 'recoil';
import {
  allBlockIdsState,
  blockExpandedRows,
  blockExpandedState,
  blockOverflowErrorState,
  computeSourcePageEffectiveLayout,
  continuationUserBlocksState,
  type ContinuationPageDescriptor,
  footerMeasurePageIndexState,
  isContinuationLayoutId,
  makeContinuationLayoutId,
  planContinuationDrop,
  RenderedPage,
  reportZoom,
  sanitizeSourcePageLayout,
  type SourcePageDescriptor,
  studioFooterHeight,
  studioMaxUsableRows,
  studioPrintOrientationType,
  viewPages,
} from 'venn-state';
import { triggerResizeEvent } from '../../logic/useSelectionSystem';
import { isEqual, isUndefined, omitBy } from 'lodash';
import styled, { css } from 'styled-components';
import { GetColor, Shimmer, ZIndex } from 'venn-ui-kit';
import ContinuationBlockWrapper from './ContinuationBlockWrapper';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DROP_ITEM_ID, useGridLayoutSettings } from './useGridLayoutSettings';

interface GridPageProps {
  /** Position in the rendered list — used for the page footer display and page number context. */
  renderedIndex: number;
  totalPages: number;
  descriptor: RenderedPage;
}

const ContinuationGridPageView = ({
  descriptor,
  renderedIndex,
  totalPages,
}: {
  descriptor: ContinuationPageDescriptor;
  renderedIndex: number;
  totalPages: number;
}) => {
  const { hasPermission } = useContext(UserContext);
  const { inPrintMode } = useAppPrintMode();
  const { onInsertBlock, draggingBlock } = useContext(StudioContext);
  const printOrientationType = useRecoilValue(studioPrintOrientationType);
  const gridSettings = dimensions[printOrientationType].gridSettings;
  const blockIds = useRecoilValue(allBlockIdsState);
  const canEditGrid = hasPermission('STUDIO_INSERT_BLOCKS');

  const { gridConfig, dragConfig, dropConfig, isDraggingOver, compactor } = useGridLayoutSettings(
    printOrientationType,
    canEditGrid,
    false,
    true,
  );

  const hasMultiPages = useHasFF('block_multi_pages');

  const pageFooterRef = useRef<HTMLDivElement | null>(null);

  const sliceLayoutId = makeContinuationLayoutId({
    sourceBlockId: descriptor.sourceBlockId,
    continuationIndex: descriptor.continuationIndex,
  });

  const pageLayout: Layout = useMemo(
    () => [
      { i: sliceLayoutId, x: 0, y: 0, w: descriptor.blockLayout.w, h: descriptor.sliceRowCount, static: true },
      ...descriptor.userBlocks,
    ],
    [sliceLayoutId, descriptor.blockLayout.w, descriptor.sliceRowCount, descriptor.userBlocks],
  );
  const pageBlockIds = useMemo(() => pageLayout.map((layout) => layout.i), [pageLayout]);

  const userBlockLayouts = useMemo(() => descriptor.userBlocks, [descriptor.userBlocks]);
  const overflowErrors = useRecoilValue(
    waitForAll(userBlockLayouts.map((layout) => blockOverflowErrorState(layout.i))),
  );

  const onPageLayoutChange = useRecoilCallback(
    ({ set }) =>
      (givenLayout: Layout) => {
        if (givenLayout.some((l) => l.i === DROP_ITEM_ID)) return;
        const newLayout = givenLayout.map((layout) => omitBy(layout, isUndefined) as LayoutItem);
        const newUserBlocks = newLayout.filter((l) => !isContinuationLayoutId(l.i));
        if (
          newUserBlocks.length !== userBlockLayouts.length ||
          newUserBlocks.some((newL, idx) => !isEqual(newL, userBlockLayouts[idx]))
        ) {
          set(continuationUserBlocksState(sliceLayoutId), newUserBlocks);
        }
      },
    [sliceLayoutId, userBlockLayouts],
  );

  const onDrop = useRecoilCallback(
    ({ snapshot, set }) =>
      async (newLayout: Layout) => {
        const newBlockLayout = newLayout.find((l) => l.i === DROP_ITEM_ID);
        if (!draggingBlock || !newBlockLayout) return;

        const existingUserBlocks = pageLayout.filter((l) => l.i !== DROP_ITEM_ID && !isContinuationLayoutId(l.i));
        const { fitsOnContinuation } = planContinuationDrop({
          existingUserBlocks,
          movedItem: { ...DROPPING_ITEM, x: 0, y: 0 },
          sliceRowCount: descriptor.sliceRowCount,
          pageRows: gridSettings.noRows,
          noColumns: gridSettings.noColumns,
        });

        if (fitsOnContinuation) {
          const newBlockId = await onInsertBlock({ label: '', value: draggingBlock }, blockIds.length);
          if (newBlockId) {
            set(continuationUserBlocksState(sliceLayoutId), (prev: Layout) => [
              ...prev,
              { ...newBlockLayout, i: newBlockId },
            ]);
          }
        } else {
          const pages = await snapshot.getPromise(viewPages);
          const sourceBlockId = descriptor.sourceBlockId;
          let lastContinuationIdx = descriptor.sourcePageIndex;
          for (let i = descriptor.sourcePageIndex + 1; i < pages.length; i++) {
            const p = pages[i]!;
            if (p.type !== PageType.CONTINUATION) break;
            if (p.continuation.sourceBlockId !== sourceBlockId) break;
            lastContinuationIdx = i;
          }
          const targetPageNumber = lastContinuationIdx + 1;
          const spilloverPage: Page = { type: PageType.GRID, layout: [] };
          set(viewPages, (current) => [
            ...current.slice(0, targetPageNumber),
            spilloverPage,
            ...current.slice(targetPageNumber),
          ]);

          onInsertBlock({ label: '', value: draggingBlock }, blockIds.length, {
            pageNumber: targetPageNumber,
            layout: [newBlockLayout],
          });
        }
      },
    [
      draggingBlock,
      pageLayout,
      descriptor.sliceRowCount,
      descriptor.sourceBlockId,
      descriptor.sourcePageIndex,
      gridSettings.noRows,
      gridSettings.noColumns,
      onInsertBlock,
      blockIds.length,
      sliceLayoutId,
    ],
  );

  const onDragOrResize = useOnDragOrResize(blockIds);
  const style = useMemo(
    () => ({ width: gridSettings.width, height: gridSettings.height, transformOrigin: 'top left' }),
    [gridSettings.width, gridSettings.height],
  );
  const originalContextValue = useContext(StudioContext);
  const contextValue = useMemo(() => ({ ...originalContextValue, inPrintMode: true }), [originalContextValue]);

  return (
    <StudioContext.Provider value={contextValue}>
      <PageHeader />
      <ContentContainer>
        <ContinuationGridContainer
          width={gridSettings.width}
          height={gridSettings.height}
          dragging={!!draggingBlock}
          draggingOver={isDraggingOver}
        >
          <ReactGridLayout
            style={style}
            width={gridSettings.width}
            autoSize={false}
            onLayoutChange={onPageLayoutChange}
            gridConfig={gridConfig}
            dragConfig={dragConfig}
            dropConfig={dropConfig}
            layout={pageLayout}
            onDrop={onDrop}
            onDragStart={onDragOrResize}
            onResizeStart={onDragOrResize}
            onResizeStop={() => triggerResizeEvent()}
            compactor={compactor}
          >
            {pageLayout.map((blockLayout, index) => {
              if (blockLayout.i === DROP_ITEM_ID) {
                if (!draggingBlock) return null;
                return <StudioShimmerBlock key={blockLayout.i} id={blockLayout.i} />;
              }

              if (isContinuationLayoutId(blockLayout.i)) {
                return (
                  <div key={blockLayout.i}>
                    <ContinuationBlockWrapper
                      sourceBlockId={descriptor.sourceBlockId}
                      continuationIndex={descriptor.continuationIndex}
                      pageNumber={renderedIndex}
                      gridSettings={gridSettings}
                      sourceBlockWidthInColumns={blockLayout.w}
                      inPrintMode={inPrintMode}
                      sliceHeightPx={descriptor.sliceHeightPx}
                      scrollOffsetPx={descriptor.scrollOffsetPx}
                      sliceStartRowIndex={descriptor.sliceStartRowIndex}
                      sliceDataHeightPx={descriptor.sliceDataHeightPx}
                    />
                  </div>
                );
              }

              const blockId = blockLayout.i;
              const userIndex = userBlockLayouts.findIndex((l) => l.i === blockId);
              return (
                <BlockOuter
                  inPrintMode={inPrintMode}
                  isReadOnly={!hasPermission('STUDIO_REORDER_BLOCKS')}
                  handleOverlapsError={overflowErrors[userIndex]?.position === 'Bottom-Right'}
                  key={blockId}
                  id={blockId}
                  hasMultiPages={hasMultiPages}
                >
                  <ReportLabBlockContainer
                    id={blockId}
                    blockLayout={blockLayout}
                    pageFooterRef={pageFooterRef}
                    pageNumber={renderedIndex}
                  >
                    <BlockContainer
                      id={blockId}
                      index={index}
                      containerWidth={calcGridItemWidth(gridSettings, blockLayout?.w)}
                    />
                  </ReportLabBlockContainer>
                </BlockOuter>
              );
            })}
          </ReactGridLayout>
        </ContinuationGridContainer>
      </ContentContainer>
      <PageFooter
        ref={pageFooterRef}
        pageBlockIds={pageBlockIds}
        currentPage={renderedIndex + 1}
        totalPage={totalPages}
        useRenderedFootnotes
      />
    </StudioContext.Provider>
  );
};

const SourceGridPageView = ({
  descriptor,
  renderedIndex,
  totalPages,
}: {
  descriptor: SourcePageDescriptor;
  renderedIndex: number;
  totalPages: number;
}) => {
  const { hasPermission } = useContext(UserContext);
  const { inPrintMode } = useAppPrintMode();
  const { onInsertBlock, draggingBlock } = useContext(StudioContext);
  const viewPageIndex = descriptor.sourceIndex;
  const page = descriptor.page;
  const updatePage = useUpdatePage(viewPageIndex);
  const printOrientationType = useRecoilValue(studioPrintOrientationType);
  const gridSettings = dimensions[printOrientationType].gridSettings;
  const blockIds = useRecoilValue(allBlockIdsState);
  const setFooterHeight = useSetRecoilState(studioFooterHeight);
  const footerMeasureIndex = useRecoilValue(footerMeasurePageIndexState);
  const isFooterMeasurePage = descriptor.sourceIndex === footerMeasureIndex;
  const zoom = useRecoilValue(reportZoom);

  const canEditGrid = hasPermission('STUDIO_INSERT_BLOCKS');
  const hasMultiPages = useHasFF('block_multi_pages');

  const pageFooterRef = useRef<HTMLDivElement | null>(null);
  const lastGestureRef = useRef<'drag' | 'resize' | null>(null);

  const { width, height } = gridSettings;
  const pageLayout: Layout = page.layout;
  const pageBlockIds = useMemo(() => pageLayout.map((layout) => layout.i), [pageLayout]);
  const overflowErrors = useRecoilValue(waitForAll(pageLayout.map((layout) => blockOverflowErrorState(layout.i))));
  const expandedStates = useRecoilValue(waitForAll(pageLayout.map((layout) => blockExpandedState(layout.i))));
  const expandedRowValues = useRecoilValue(waitForAll(pageLayout.map((layout) => blockExpandedRows(layout.i))));
  const hasAnyExpanded = expandedStates.some(Boolean);
  const maxUsableRows = useRecoilValue(studioMaxUsableRows);

  const { gridConfig, dragConfig, dropConfig, isDraggingOver, compactor } = useGridLayoutSettings(
    printOrientationType,
    canEditGrid,
    hasAnyExpanded,
    hasAnyExpanded,
  );

  const onPageLayoutChange = useCallback(
    (givenLayout: Layout) => {
      const persistedById = new Map(page.layout.map((layout) => [layout.i, layout]));
      const expandedIds = new Set(page.layout.filter((_, idx) => expandedStates[idx]).map((layout) => layout.i));
      const newLayout = sanitizeSourcePageLayout({ givenLayout, persistedById, expandedIds });

      // Block membership on a page only changes through explicit actions (insert / delete /
      // Outline drag), never through a layout-change callback. RGL emits a partial layout on its
      // first sync after a block is added to this page from the Outline (the new child isn't in
      // RGL's internal layout yet); persisting that report would delete the just-dropped block.
      // Ignore any report that is missing a persisted block.
      const reportedIds = new Set(newLayout.map((layout) => layout.i));
      if (page.layout.some((layout) => !reportedIds.has(layout.i))) return;

      const sized =
        lastGestureRef.current === 'drag'
          ? newLayout.map((item) => {
              const persisted = persistedById.get(item.i);
              return persisted ? { ...item, w: persisted.w, h: persisted.h } : item;
            })
          : newLayout;
      const changed =
        sized.length !== page.layout.length || sized.some((layout) => !isEqual(layout, persistedById.get(layout.i)));
      if (changed) updatePage({ layout: sized });
    },
    [updatePage, page.layout, expandedStates],
  );

  const onDrop = useCallback(
    (newLayout: Layout) => {
      const dropped = newLayout.find((l) => l.i === DROP_ITEM_ID);
      if (hasAnyExpanded && dropped && dropped.y + dropped.h > maxUsableRows) return;

      if (draggingBlock) {
        const persistedById = new Map(page.layout.map((layout) => [layout.i, layout]));
        const expandedIds = new Set(page.layout.filter((_, idx) => expandedStates[idx]).map((layout) => layout.i));
        const sanitized = sanitizeSourcePageLayout({ givenLayout: newLayout, persistedById, expandedIds });
        onInsertBlock({ label: '', value: draggingBlock }, blockIds.length, {
          pageNumber: viewPageIndex,
          layout: sanitized,
        });
      }
      analyticsService.ctaClicked({
        purpose: 'insert studio block',
        locationOnPage: `${draggingBlock?.customBlockType} block dragged to page`,
      });
    },
    [
      draggingBlock,
      onInsertBlock,
      viewPageIndex,
      blockIds.length,
      page.layout,
      expandedStates,
      hasAnyExpanded,
      maxUsableRows,
    ],
  );

  const effectiveLayout = useMemo(
    () => computeSourcePageEffectiveLayout({ pageLayout, expandedStates, expandedRows: expandedRowValues }),
    [pageLayout, expandedStates, expandedRowValues],
  );

  const style = useMemo(() => ({ width, height, transformOrigin: 'top left' }), [width, height]);

  const originalContextValue = useContext(StudioContext);
  const contextValue = useMemo(() => ({ ...originalContextValue, inPrintMode: true }), [originalContextValue]);

  const onDragOrResize = useOnDragOrResize(blockIds);

  const handleDragStart = useCallback(
    (layout: Layout, oldItem: LayoutItem | null, newItem: LayoutItem | null) => {
      lastGestureRef.current = 'drag';
      onDragOrResize(layout, oldItem, newItem);
    },
    [onDragOrResize],
  );

  const handleResizeStart = useCallback(
    (layout: Layout, oldItem: LayoutItem | null, newItem: LayoutItem | null) => {
      lastGestureRef.current = 'resize';
      onDragOrResize(layout, oldItem, newItem);
    },
    [onDragOrResize],
  );

  return (
    <StudioContext.Provider value={contextValue}>
      <PageHeader />
      <ContentContainer>
        <GridContainer
          width={gridSettings.width}
          height={gridSettings.height}
          dragging={!!draggingBlock}
          draggingOver={isDraggingOver}
        >
          <ReactGridLayout
            style={style}
            width={width}
            autoSize={false}
            onLayoutChange={onPageLayoutChange}
            gridConfig={gridConfig}
            dragConfig={dragConfig}
            dropConfig={dropConfig}
            layout={effectiveLayout}
            onDrop={onDrop}
            onDragStart={handleDragStart}
            onResizeStart={handleResizeStart}
            onResizeStop={() => triggerResizeEvent()}
            compactor={compactor}
          >
            {pageLayout.map((blockLayout, index) => {
              if (blockLayout.i === DROP_ITEM_ID) {
                if (!draggingBlock) {
                  // If the thing being dragged is not a block, do not render
                  return null;
                }
                return <StudioShimmerBlock key={blockLayout.i} id={blockLayout.i} />;
              }

              const blockId = blockLayout.i;

              return (
                <BlockOuter
                  inPrintMode={inPrintMode}
                  isReadOnly={!hasPermission('STUDIO_REORDER_BLOCKS') || !!expandedStates[index]}
                  handleOverlapsError={overflowErrors[index]?.position === 'Bottom-Right'}
                  key={blockId}
                  id={blockId}
                  hasMultiPages={hasMultiPages}
                >
                  <ReportLabBlockContainer
                    id={blockId}
                    blockLayout={blockLayout}
                    pageFooterRef={pageFooterRef}
                    pageNumber={renderedIndex}
                  >
                    <BlockContainer
                      id={blockId}
                      index={index}
                      containerWidth={calcGridItemWidth(gridSettings, blockLayout?.w)}
                    />
                  </ReportLabBlockContainer>
                </BlockOuter>
              );
            })}
          </ReactGridLayout>
        </GridContainer>
      </ContentContainer>
      <PageFooter
        ref={pageFooterRef}
        pageBlockIds={pageBlockIds}
        currentPage={renderedIndex + 1}
        totalPage={totalPages}
        setStudioFooterHeight={isFooterMeasurePage ? setFooterHeight : undefined}
        useRenderedFootnotes
        scale={zoom}
      />
    </StudioContext.Provider>
  );
};

const isContinuationDescriptor = RenderedPage.$is('ContinuationPage');

/** Renders a single report page — either a source GRID page or a continuation slice page. */
const GridPage = ({ renderedIndex, descriptor, totalPages }: GridPageProps) => {
  if (isContinuationDescriptor(descriptor)) {
    return <ContinuationGridPageView descriptor={descriptor} renderedIndex={renderedIndex} totalPages={totalPages} />;
  }
  return <SourceGridPageView descriptor={descriptor} renderedIndex={renderedIndex} totalPages={totalPages} />;
};

export default GridPage;

const GridContainer = styled.div<{
  dragging: boolean;
  draggingOver: boolean;
  width: number;
  height: number;
}>`
  ${({ dragging, draggingOver }) =>
    dragging &&
    css`
      outline: 2px dashed ${GetColor.GreyScale.Grey40};
      ${!draggingOver &&
      css`
        > div {
          opacity: 0.2;
        }

        ::before {
          content: 'Drop here';
          top: 50%;
          left: 50%;
          transform: translate(-50%, -100%);
          font-size: 50px;
          position: absolute;
          z-index: ${ZIndex.Front};
        }
      `}
    `}
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

const BlockOuter = styled.div<{
  handleOverlapsError: boolean;
  inPrintMode: boolean;
  isReadOnly?: boolean;
  hasMultiPages: boolean;
}>`
  ${({ inPrintMode }) =>
    inPrintMode &&
    css`
      .react-resizable-handle {
        display: none;
      }
    `}
  ${({ inPrintMode, isReadOnly, theme, hasMultiPages, handleOverlapsError }) =>
    !inPrintMode &&
    !isReadOnly &&
    (hasMultiPages
      ? css`
          .react-resizable-handle {
            background-image: none;
            z-index: ${ZIndex.Cover};
            bottom: 0;
            right: 0;
            cursor: se-resize;

            ::after {
              z-index: ${ZIndex.Cover};
              content: '';
              position: absolute;
              right: -1.5px;
              bottom: -1.5px;
              width: 12px;
              height: 12px;
              background-image: ${`url("data:image/svg+xml,${encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'>
<path d='M4 17H11A6 6 0 0 0 17 11V4' stroke='${GetColor.GreyScale.Grey60({ theme })}' stroke-width='8' stroke-linecap='round' stroke-linejoin='round'/>
</svg>`,
              )}")`};
              background-repeat: no-repeat;
              background-size: 12px 12px;
              border: none;
            }
          }
        `
      : css`
          .react-resizable-handle {
            background-image: none;
            z-index: ${ZIndex.Cover};
            bottom: 0;
            right: 0;
            cursor: se-resize;

            ::after {
              z-index: ${ZIndex.Cover};
              content: '';
              position: absolute;
              right: 5px;
              bottom: 5px;
              width: 15px;
              height: 15px;
              border-right: 4px solid
                ${handleOverlapsError
                  ? css`
                      ${GetColor.White}
                    `
                  : 'rgba(0, 0, 0, 0.6)'};
              border-bottom: 4px solid
                ${handleOverlapsError
                  ? css`
                      ${GetColor.White}
                    `
                  : 'rgba(0, 0, 0, 0.6)'};
            }
          }
        `)}
`;

const ContentContainer = styled.div`
  position: relative;
  flex: 1;

  .react-grid-placeholder.react-grid-placeholder {
    ${Shimmer};
    animation-duration: 5s;
    z-index: ${ZIndex.Base};
    opacity: 1;
  }

  .react-grid-item {
    z-index: ${ZIndex.Cover};
  }

  .${SpecialCssClasses.NotDownloadable} {
    display: none;
  }
`;

const ContinuationGridContainer = styled(GridContainer)`
  ${({ dragging }) =>
    dragging &&
    css`
      outline-color: ${GetColor.GreyScale.Grey30};
    `}
`;
